import { useState, useEffect } from 'react'
import { ImportSession, DataEntity } from '@retailsync/shared-types'
import type { ConnectorEngine, MappedData } from '../../services/connector-engine'

interface DataPreviewStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
  engine: ConnectorEngine | null
  setIsProcessing: (v: boolean) => void
}

export function DataPreviewStep({ session, onUpdate, engine, setIsProcessing }: DataPreviewStepProps) {
  const [mappedData, setMappedData] = useState<MappedData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const STEP_ORDER = ['upload', 'format', 'mapping', 'preview', 'import'] as const
  type StepType = typeof STEP_ORDER[number]

  const getCurrentStepIndex = () => STEP_ORDER.indexOf(session.currentStep as StepType)
  const getNextStep = () => STEP_ORDER[getCurrentStepIndex() + 1] as StepType
  const getPreviousStep = () => STEP_ORDER[getCurrentStepIndex() - 1] as StepType

  useEffect(() => {
    if (engine && session.rawData && session.mappings && session.selectedTemplate && !mappedData) {
      loadPreviewData()
    }
  }, [engine, session.rawData, session.mappings, session.selectedTemplate])

  const loadPreviewData = async () => {
    if (!engine || !session.rawData || !session.mappings || !session.selectedTemplate) return

    setLoading(true)
    setError(null)
    setIsProcessing(true)

    try {
      const entity = (session.selectedTemplate.entity || 'products') as DataEntity
      const parsedFile = {
        headers: session.rawData.length > 0 ? Object.keys(session.rawData[0]) : [],
        rows: session.rawData,
        totalRows: session.rawData.length,
        fileName: session.uploadedFile?.name || 'unknown',
        fileSize: session.uploadedFile?.size || 0,
        fileType: 'csv' as const,
      }

      const result = await engine.mapData(parsedFile, entity, session.mappings)
      setMappedData(result)
      onUpdate({ previewData: result.records })
    } catch (err) {
      console.error('Preview error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load preview data')
    } finally {
      setLoading(false)
      setIsProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Mapping and previewing data...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">❌ {error}</div>
        <button
          onClick={loadPreviewData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!mappedData) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">No preview data available. Ensure a file is uploaded and field mappings are configured.</div>
        <button
          onClick={loadPreviewData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Generate Preview
        </button>
      </div>
    )
  }

  // Build display: show first 5 mapped records
  const previewRecords = mappedData.records.slice(0, 5)
  const allKeys = previewRecords.length > 0 ? Object.keys(previewRecords[0]) : []

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Data Preview</h3>
        <p className="text-gray-600 mb-6">
          Review your mapped data before importing. Showing {previewRecords.length} of {mappedData.records.length} records mapped to <strong>{mappedData.entity}</strong>.
        </p>
      </div>

      {/* Mapped Records Table */}
      {previewRecords.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-700">#</th>
                {allKeys.map(key => (
                  <th key={key} className="px-4 py-2 text-left font-medium text-gray-700">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {previewRecords.map((record, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                  {allKeys.map(key => (
                    <td key={key} className="px-4 py-2 text-gray-700 max-w-[200px] truncate">
                      {String(record[key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-green-600 mr-2">✓</span>
          <div>
            <h3 className="text-sm font-medium text-green-800">Mapping Complete</h3>
            <p className="mt-1 text-sm text-green-700">
              {mappedData.records.length} records mapped to <strong>{mappedData.entity}</strong> using {mappedData.mappings.length} field mappings.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={() => onUpdate({ currentStep: getPreviousStep() })}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ← Back to Mapping
        </button>
        <button
          onClick={() => onUpdate({ currentStep: getNextStep() })}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Continue to Import →
        </button>
      </div>
    </div>
  )
}